import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { useOnboarding } from '../hooks/useOnboarding'
import OnboardingWizard from '../components/OnboardingWizard'
import OnboardingStep from '../components/OnboardingStep'

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  toolsUsed?: string[]
}

const ORG_TYPE_OPTIONS = [
  { value: 'urheilu', label: '🏆 Urheiluseura' },
  { value: 'kulttuuri', label: '🎭 Kulttuuriyhdistys' },
  { value: 'asukas', label: '🏘️ Asukasyhdistys' },
  { value: 'ammatti', label: '💼 Ammattiyhdistys' },
  { value: 'hyvinvointi', label: '❤️ Hyvinvointi / sosiaali' },
  { value: 'muu', label: '📋 Muu yhdistys' },
]

export default function OnboardingPage() {
  const navigate = useNavigate()
  const { activeOrg } = useAuthStore()
  const {
    progress,
    steps,
    loading,
    isComplete,
    completeStep,
    updateOrgType,
    sendAiMessage,
    executeAiAction,
  } = useOnboarding()

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const [orgTypeSelected, setOrgTypeSelected] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isComplete) {
      navigate('/dashboard')
    }
  }, [isComplete, navigate])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  // Send initial AI greeting when org type is set
  useEffect(() => {
    if (progress && orgTypeSelected && chatMessages.length === 0) {
      sendInitialGreeting()
    }
    // Only fire on orgTypeSelected change
  }, [orgTypeSelected])

  const sendInitialGreeting = async () => {
    setChatLoading(true)
    try {
      const data = await sendAiMessage(
        'Hei! Olen juuri aloittanut. Mitä pitää tehdä ensimmäiseksi?',
        []
      )
      setChatMessages([
        {
          id: `user-${Date.now()}`,
          role: 'user',
          content: 'Hei! Olen juuri aloittanut. Mitä pitää tehdä ensimmäiseksi?',
        },
        {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          content: data.response,
          toolsUsed: data.tools_used,
        },
      ])
    } catch {
      // silent fail for initial greeting
    } finally {
      setChatLoading(false)
    }
  }

  const handleSendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: chatInput,
    }
    const history = chatMessages.map(m => ({ role: m.role, content: m.content }))
    setChatMessages(m => [...m, userMsg])
    setChatInput('')
    setChatLoading(true)
    try {
      const data = await sendAiMessage(chatInput, history)
      setChatMessages(m => [
        ...m,
        {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          content: data.response,
          toolsUsed: data.tools_used,
        },
      ])
    } catch (e) {
      setChatMessages(m => [
        ...m,
        { id: `err-${Date.now()}`, role: 'assistant', content: `Virhe: ${String(e)}` },
      ])
    } finally {
      setChatLoading(false)
    }
  }

  const handleOrgTypeSelect = async (orgType: string) => {
    await updateOrgType(orgType)
    setOrgTypeSelected(true)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Ladataan...</div>
      </div>
    )
  }

  if (!activeOrg || !progress) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Organisaatiota ei löydy.</div>
      </div>
    )
  }

  // Step 0: Select org type if not set
  if (!progress.org_type && !orgTypeSelected) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="text-4xl mb-3">👋</div>
            <h1 className="text-2xl font-bold text-gray-900">Tervetuloa Kirstunvartijaan!</h1>
            <p className="text-gray-500 mt-2">
              Aloitetaan perustasoittain. Minkä tyyppinen yhdistys {activeOrg.name} on?
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {ORG_TYPE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleOrgTypeSelect(opt.value)}
                className="p-4 border-2 border-gray-200 rounded-xl text-left hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <div className="text-lg">{opt.label.split(' ')[0]}</div>
                <div className="text-sm text-gray-700 mt-1">{opt.label.substring(opt.label.indexOf(' ') + 1)}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const currentStepKey = progress.current_step
  const currentStep = steps.find(s => s.key === currentStepKey)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Aloitus — {activeOrg.name}</h1>
              <p className="text-sm text-gray-500">Käydään läpi muutama perusvaihe</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-gray-400 hover:text-gray-600"
            >
              Jatka myöhemmin →
            </button>
          </div>
          {steps.length > 0 && <OnboardingWizard steps={steps} progress={progress} />}
        </div>
      </div>

      {/* Main content: steps + chat side by side */}
      <div className="max-w-5xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Steps */}
          <div className="space-y-3">
            <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wide mb-3">Vaiheet</h2>
            {steps.filter(s => s.key !== 'completed').map(step => {
              const isStepCompleted = isStepDone(step.key, progress)
              const isActive = step.key === currentStepKey
              return (
                <OnboardingStep
                  key={step.key}
                  step={step}
                  progress={progress}
                  isActive={isActive}
                  isCompleted={isStepCompleted}
                  onComplete={async (skip) => { await completeStep(step.key, skip) }}
                  onExecuteAction={executeAiAction}
                />
              )
            })}

            {/* Completed state */}
            {currentStepKey === 'completed' && (
              <div className="p-6 rounded-xl bg-green-50 border border-green-200 text-center">
                <div className="text-4xl mb-2">🎉</div>
                <h3 className="text-lg font-bold text-green-800">Kaikki vaiheet tehty!</h3>
                <p className="text-green-600 mt-1 mb-4">Olet valmis käyttämään Kirstunvartijaa.</p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="bg-green-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-green-700"
                >
                  Siirry dashboardille
                </button>
              </div>
            )}
          </div>

          {/* Right: AI Chat */}
          <div className="flex flex-col bg-white rounded-xl border border-gray-200 shadow-sm" style={{ height: '500px' }}>
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400"></div>
              <span className="font-medium text-sm text-gray-700">AI-avustaja</span>
              {currentStep && (
                <span className="text-xs text-gray-400 ml-1">· {currentStep.title}</span>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.length === 0 && !chatLoading && (
                <div className="text-center text-gray-400 text-sm mt-8">
                  <div className="text-2xl mb-2">💬</div>
                  <p>Kysy AI-avustajalta apua tai anna sen ohjata sinut läpi vaiheiden.</p>
                </div>
              )}
              {chatMessages.map(msg => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm
                      ${msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-800'}`}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                    {msg.toolsUsed && msg.toolsUsed.length > 0 && (
                      <div className="mt-1 text-xs opacity-60">
                        🔧 {msg.toolsUsed.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl px-4 py-2 text-sm text-gray-500">
                    <span className="animate-pulse">Kirjoittaa...</span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-gray-100">
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder="Kysy jotain..."
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  disabled={chatLoading}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={chatLoading || !chatInput.trim()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function isStepDone(stepKey: string, progress: ReturnType<typeof useOnboarding>['progress']): boolean {
  if (!progress) return false
  switch (stepKey) {
    case 'fiscal_period': return !!progress.step_fiscal_period_completed
    case 'chart_of_accounts': return !!progress.step_chart_of_accounts_completed
    case 'first_transaction': return !!progress.step_first_transaction_completed
    default: return false
  }
}
